import type { ColDef, ValueFormatterParams } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { defaultColDef, gridTheme } from '../grid'

/**
 * Whether the Exam has this row's Topic Code as a Topic.
 *
 * This is the check the platform makes instead of a foreign key. A Question carries a Topic
 * Code as text and is never joined to a Topic, because a Topic dropped from the Exam must not
 * take the Questions written for it with it. So a code the Exam does not have is reported
 * here rather than refused at the door.
 */
export type Filing = 'A Topic' | 'Not a Topic' | 'Unfiled'

export interface TopicRow {
  topicCode: string | null
  topic: string | null
  filing: Filing
  generated: number
  generatedShare: number | null
}

export interface TopicsGridProps {
  rows: TopicRow[]
  /** Pinned to the foot by the grid, so it stays put while the rows scroll. */
  total: TopicRow
  pending: boolean
}

const columns: ColDef<TopicRow>[] = [
  {
    field: 'topicCode',
    headerName: 'Topic Code',
    width: 150,
    pinned: 'left',
    // Named, so an unfiled Question is a row someone can act on rather than a blank.
    valueFormatter: (cell) => cell.value ?? 'No Topic Code',
    cellClass: (cell) => (cell.value ? 'font-bold' : 'text-muted'),
  },
  { field: 'topic', headerName: 'Topic', flex: 1, minWidth: 200 },
  {
    field: 'filing',
    headerName: 'Filing',
    width: 155,
    // A word, not a colour: WCAG 1.4.1 will not have colour carrying the distinction
    // by itself, and this column is the whole point of the screen.
    //
    // Blank on the totals row, which is not filed anywhere: a pinned row reading "A Topic"
    // would be answering a question nobody asked of a total.
    valueFormatter: (cell) => (cell.node?.rowPinned ? '' : String(cell.value)),
    cellClass: (cell) =>
      cell.node?.rowPinned || cell.value === 'A Topic' ? '' : 'font-bold text-red-700',
    headerTooltip:
      'Whether the Exam has this Topic Code as a Topic. A Question citing a code the Exam does not have is reported, never refused.',
  },
  {
    field: 'generated',
    headerName: 'Original',
    width: 130,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
  },
  {
    field: 'generatedShare',
    headerName: 'Original %',
    width: 145,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: percent,
    headerTooltip: "This Topic's share of every Original Question for the Exam.",
  },
]

/**
 * The Exam's Topics and the Bank in one grid: every Topic the specification lists, every
 * Topic Code the Bank cites, and how much of the Bank sits under each. Read-only: the Topics
 * are written under the Exam menu.
 *
 * The Questions Manager's grid, from the same theme and the same defaults (`./grid`):
 * sorting, the filter row under each heading and the pinned totals row are AG Grid's
 * rather than this screen's (app:R14).
 *
 * It lays itself out to its rows -- an Exam is tens of Topics, not thousands -- so
 * there is no paging and no fixed height leaving a half-empty frame under four rows.
 */
export function TopicsGrid({ rows, total, pending }: TopicsGridProps) {
  return (
    <div className="mt-4 w-full">
      <AgGridReact<TopicRow>
        theme={gridTheme}
        domLayout="autoHeight"
        columnDefs={columns}
        defaultColDef={defaultColDef}
        rowData={pending ? undefined : rows}
        loading={pending}
        getRowId={(row) => row.data.topicCode ?? ''}
        pinnedBottomRowData={rows.length > 0 ? [total] : undefined}
        overlayNoRowsTemplate="No Topic for the Exam and no Question in the Bank, so there is nothing to show."
        tooltipShowDelay={400}
      />
    </div>
  )
}

/** Whole percentages: a tenth of a point would suggest a precision these counts do not have. */
function percent(cell: ValueFormatterParams<TopicRow, number | null>): string {
  return cell.value === null || cell.value === undefined ? '' : `${Math.round(cell.value)}%`
}
